/**
 * GestionMedicosPage — Unificada (M5)
 *
 * Pantalla única para gestionar el ciclo de vida completo de los médicos:
 *   • Activos        — médicos registrados con acceso al portal.
 *   • Inactivos      — médicos desactivados (acceso revocado).
 *   • Pre-registrados — invitaciones pendientes de activación por el médico.
 *
 * Reemplaza la antigua PreRegistroPage (ahora redirige aquí). El admin
 * crea un pre-registro mediante el botón "+ Pre-registrar médico" que
 * abre `PreRegistroFormModal`.
 */
import { useState, useMemo, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { orderBy } from 'firebase/firestore';
import {
  AlertTriangle,
  Clock,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  UserCheck,
  Users,
  UserX,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Avatar } from '../../components/ui/Avatar';
import { PreRegistroFormModal } from '../../components/admin/PreRegistroFormModal';
import { EditarMedicoModal } from '../../components/admin/EditarMedicoModal';
import { functions } from '../../config/firebase';
import { useCollection } from '../../hooks/useFirestore';
import { useMedicosConLatido } from '../../hooks/useMedicosConLatido';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import type { MedicoCompleto, MedicoPreRegistro, RolUsuario } from '../../types';
import styles from './GestionMedicosPage.module.css';

const asignarRolFn = httpsCallable(functions, 'portal_asignarRol');

type ActionKind = 'desactivar' | 'reactivar' | 'cambiar_rol';
type TabKey = 'activos' | 'inactivos' | 'preregistros';

interface ConfirmState {
  medico: MedicoCompleto;
  action: ActionKind;
  title: string;
  description: string;
  confirmLabel: string;
  variant: 'danger' | 'primary' | 'secondary';
  /** Solo para cambiar_rol: rol seleccionado en el selector del modal. */
  nuevoRol?: RolUsuario;
}

const ROLES_ASIGNABLES: { value: RolUsuario; label: string; hint: string }[] = [
  { value: 'medico', label: 'Médico', hint: 'Atiende consultas. Ve su dashboard, salas e historial.' },
  { value: 'supervisor', label: 'Supervisor', hint: 'Supervisa la operación: todas las opciones excepto Prompts IA y Configuración IA. También atiende (salas e historial).' },
  { value: 'admin', label: 'Administrador', hint: 'Configura todo el sistema (incl. IA). No atiende pacientes (sin salas ni historial).' },
  { value: 'notificador', label: 'Gestión de notificaciones', hint: 'Solo accede al módulo de campañas push (crear, programar y medir notificaciones). No atiende pacientes.' },
  { value: 'visor', label: 'Visor de monitoreo', hint: 'Solo lectura, para los televisores de sala: ve los tableros /tv y nada más. Sin acceso a expedientes, colas ni configuración.' },
];

function labelRol(role: string): string {
  return ROLES_ASIGNABLES.find((r) => r.value === role)?.label ?? 'Médico';
}

export default function GestionMedicosPage() {
  const { addToast } = useToast();
  /* Editar, desactivar o eliminar usuarios está permitido a ADMINISTRADORES y
     SUPERVISORES en el backend (editarMedico.ts, asignarRol.ts). Aquí la
     interfaz se limita a reflejar la regla que ya aplica el servidor. */
  const { role } = useAuth();
  const puedeAdministrar = role === 'admin' || role === 'supervisor';

  // ── Data sources ─────────────────────────────────────────
  const { data: medicosRaw, loading: loadingMedicos } = useMedicosConLatido<MedicoCompleto>(
    [orderBy('nombreCompleto', 'asc')],
  );
  const medicos = medicosRaw || [];

  const { data: preRegistrosRaw, loading: loadingPre } = useCollection<MedicoPreRegistro>(
    'medicos_preregistro',
  );
  const preRegistros = (preRegistrosRaw || []).filter((pr) => pr.estado === 'pendiente');

  // ── UI state ─────────────────────────────────────────────
  // Reloj para refrescar la frescura del heartbeat (Offline tras 2 min sin latido).
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const [tab, setTab] = useState<TabKey>('activos');
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [showPreRegistroModal, setShowPreRegistroModal] = useState(false);
  const [editTarget, setEditTarget] = useState<
    | { tipo: 'medico'; data: MedicoCompleto }
    | { tipo: 'preregistro'; data: MedicoPreRegistro }
    | null
  >(null);
  const [eliminarPreRegistro, setEliminarPreRegistro] = useState<MedicoPreRegistro | null>(null);
  const eliminarPreRegistroFn = useMemo(
    () => httpsCallable(functions, 'portal_eliminarMedicoPreRegistro'),
    [],
  );

  // ── Counts (always visible regardless of tab) ────────────
  const counts = useMemo(
    () => ({
      activos: medicos.filter((m) => m.activo).length,
      inactivos: medicos.filter((m) => !m.activo).length,
      preregistros: preRegistros.length,
    }),
    [medicos, preRegistros],
  );

  // ── Filtered data per tab ───────────────────────────────
  const filteredMedicos = useMemo(() => {
    const q = search.toLowerCase();
    const matches = (m: MedicoCompleto) =>
      m.nombreCompleto?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.colegiadoId?.toLowerCase().includes(q) ||
      m.especialidad?.toLowerCase().includes(q);

    const byEstado = tab === 'activos'
      ? medicos.filter((m) => m.activo)
      : tab === 'inactivos'
        ? medicos.filter((m) => !m.activo)
        : [];

    return q ? byEstado.filter(matches) : byEstado;
  }, [medicos, search, tab]);

  const filteredPreRegistros = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return preRegistros;
    return preRegistros.filter(
      (pr) =>
        `${pr.nombre} ${pr.apellidos}`.toLowerCase().includes(q) ||
        pr.email?.toLowerCase().includes(q) ||
        pr.colegiadoId?.toLowerCase().includes(q) ||
        pr.especialidad?.toLowerCase().includes(q),
    );
  }, [preRegistros, search]);

  // ── Actions ──────────────────────────────────────────────
  const handleEliminarPreRegistro = async (pr: MedicoPreRegistro) => {
    setActionLoading(pr.id);
    try {
      await eliminarPreRegistroFn({ preRegistroId: pr.id });
      addToast('success', `Pre-registro de ${pr.nombre} ${pr.apellidos} eliminado`);
    } catch (err) {
      addToast(
        'error',
        err instanceof Error ? err.message : 'Error al eliminar pre-registro',
      );
    } finally {
      setActionLoading(null);
      setEliminarPreRegistro(null);
    }
  };

  const executeAction = async (medicoUid: string, action: ActionKind, nuevoRol?: RolUsuario) => {
    setActionLoading(medicoUid);
    try {
      const result = await asignarRolFn({ medicoUid, action, nuevoRol });
      const data = result.data as { message: string };
      addToast('success', data.message);
    } catch (err: unknown) {
      addToast('error', err instanceof Error ? err.message : 'Error al ejecutar acción');
    } finally {
      setActionLoading(null);
      setConfirm(null);
    }
  };

  const requestDeactivate = (m: MedicoCompleto) =>
    setConfirm({
      medico: m,
      action: 'desactivar',
      title: 'Desactivar usuario',
      description: `¿Está seguro que desea desactivar a ${m.nombreCompleto}? El usuario perderá acceso al portal y no podrá iniciar sesión hasta que sea reactivado. Esta acción se registra en auditoría.`,
      confirmLabel: 'Sí, desactivar',
      variant: 'danger',
    });

  const requestReactivate = (m: MedicoCompleto) =>
    setConfirm({
      medico: m,
      action: 'reactivar',
      title: 'Reactivar usuario',
      description: `¿Desea reactivar a ${m.nombreCompleto}? El usuario podrá volver a iniciar sesión y acceder según su rol.`,
      confirmLabel: 'Reactivar acceso',
      variant: 'primary',
    });

  const requestRoleChange = (m: MedicoCompleto) => {
    setConfirm({
      medico: m,
      action: 'cambiar_rol',
      title: 'Cambiar rol',
      description: `Seleccione el rol para ${m.nombreCompleto}. El cambio aplica en su próximo inicio de sesión (o al refrescar sus credenciales).`,
      confirmLabel: 'Guardar rol',
      variant: 'secondary',
      nuevoRol: (m.role as RolUsuario) ?? 'medico',
    });
  };

  // ── Badges ───────────────────────────────────────────────
  const rolBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="info" size="sm">Administrador</Badge>;
      case 'supervisor':
        return <Badge variant="warning" size="sm">Supervisor</Badge>;
      case 'notificador':
        return <Badge variant="success" size="sm">Notificaciones</Badge>;
      case 'visor':
        return <Badge variant="neutral" size="sm">Visor</Badge>;
      default:
        return <Badge variant="neutral" size="sm">Médico</Badge>;
    }
  };

  // El estado real de conexión se deriva del LATIDO (ultimoHeartbeat): el campo
  // `estado` queda stale si el médico cierra el navegador sin desconectarse.
  const estadoBadge = (m: MedicoCompleto) => {
    if (!m.activo) return <Badge variant="error" size="sm">Desactivado</Badge>;
    const hb = m.ultimoHeartbeat?.toMillis?.() ?? 0;
    const online = nowTick - hb < 120_000; // 2 min
    if (!online) return <Badge variant="neutral" size="sm">Offline</Badge>;
    const est = m.estado as string;
    if (est === 'en_consulta' || est === 'charting' || est === 'buffer')
      return <Badge variant="success" size="sm">En consulta</Badge>;
    if (est === 'pausa' || est === 'break_almuerzo')
      return <Badge variant="warning" size="sm">En pausa</Badge>;
    if (est === 'disponible')
      return <Badge variant="success" size="sm">Disponible</Badge>;
    return <Badge variant="info" size="sm">Conectado</Badge>;
  };

  const loading = loadingMedicos || loadingPre;
  const showingPreRegistros = tab === 'preregistros';

  return (
    <MainLayout>
      <div className={styles.page}>
        {/* ─── Header ─── */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Gestión de Usuarios</h1>
            <p className={styles.subtitle}>
              Administre el ciclo completo: pre-registros, usuarios activos y desactivaciones.
            </p>
          </div>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setShowPreRegistroModal(true)}
          >
            <Plus size={16} aria-hidden="true" />
            Pre-registrar usuario
          </button>
        </div>

        {/* ─── Tabs ─── */}
        <div className={styles.tabs} role="tablist" aria-label="Filtrar por estado">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'activos'}
            className={`${styles.tab} ${tab === 'activos' ? styles.tabActive : ''}`}
            onClick={() => setTab('activos')}
          >
            <Users size={14} aria-hidden="true" />
            <span>Activos</span>
            <span className={styles.tabCount}>{counts.activos}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'preregistros'}
            className={`${styles.tab} ${tab === 'preregistros' ? styles.tabActive : ''}`}
            onClick={() => setTab('preregistros')}
          >
            <Clock size={14} aria-hidden="true" />
            <span>Pre-registrados</span>
            <span className={styles.tabCount}>{counts.preregistros}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'inactivos'}
            className={`${styles.tab} ${tab === 'inactivos' ? styles.tabActive : ''}`}
            onClick={() => setTab('inactivos')}
          >
            <UserX size={14} aria-hidden="true" />
            <span>Inactivos</span>
            <span className={styles.tabCount}>{counts.inactivos}</span>
          </button>
        </div>

        {/* ─── Search ─── */}
        <div className={styles.searchBar}>
          <Input
            placeholder={
              showingPreRegistros
                ? 'Buscar pre-registros por nombre, email, colegiado…'
                : 'Buscar por nombre, email, colegiado o especialidad…'
            }
            leftIcon={<Search size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* ─── Table ─── */}
        <div className={styles.tableCard}>
          {loading ? (
            <p className={styles.loading}>Cargando…</p>
          ) : showingPreRegistros ? (
            filteredPreRegistros.length === 0 ? (
              <p className={styles.empty}>
                {search
                  ? 'No se encontraron pre-registros con ese criterio.'
                  : 'No hay usuarios pre-registrados pendientes. Use "+ Pre-registrar usuario" para invitar uno.'}
              </p>
            ) : (
              <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Colegiado</th>
                      <th>Especialidad</th>
                      <th>Centro</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th className={styles.actionsCol}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPreRegistros.map((pr) => (
                      <tr key={pr.id}>
                        <td>
                          <div className={styles.medicoCell}>
                            <div className={styles.avatar} aria-hidden="true">
                              {(pr.nombre?.[0] || '') + (pr.apellidos?.[0] || '')}
                            </div>
                            <div>
                              <div className={styles.medicoName}>
                                {pr.nombre} {pr.apellidos}
                              </div>
                              <div className={styles.medicoEmail}>{pr.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>{pr.colegiadoId}</td>
                        <td>{pr.especialidad}</td>
                        <td>{pr.centroMedico}</td>
                        <td>{rolBadge(pr.role)}</td>
                        <td>
                          <Badge variant="warning" size="sm">
                            Pendiente
                          </Badge>
                        </td>
                        <td>
                          <div className={styles.actions}>
                            {!puedeAdministrar && <span className={styles.soloAdmin}>Solo administradores y supervisores</span>}
                            {puedeAdministrar && (<>
                            <button
                              type="button"
                              className={styles.actionBtn}
                              onClick={() => setEditTarget({ tipo: 'preregistro', data: pr })}
                              title="Editar pre-registro"
                            >
                              <Pencil size={14} aria-hidden="true" />
                              <span>Editar</span>
                            </button>
                            <button
                              type="button"
                              className={styles.actionBtnDanger}
                              disabled={actionLoading === pr.id}
                              onClick={() => setEliminarPreRegistro(pr)}
                              title="Eliminar pre-registro"
                            >
                              <Trash2 size={14} aria-hidden="true" />
                              <span>Eliminar</span>
                            </button>
                            </>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : filteredMedicos.length === 0 ? (
            <p className={styles.empty}>
              {search
                ? 'No se encontraron usuarios con ese criterio.'
                : tab === 'inactivos'
                  ? 'No hay usuarios desactivados.'
                  : 'No hay usuarios activos.'}
            </p>
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Médico</th>
                    <th>Colegiado</th>
                    <th>Especialidad</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Salas</th>
                    <th className={styles.actionsCol}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMedicos.map((m) => (
                    <tr key={m.id} className={!m.activo ? styles.rowDisabled : ''}>
                      <td>
                        <div className={styles.medicoCell}>
                          <Avatar
                            className={styles.avatar}
                            nombre={m.nombreCompleto || `${m.nombre ?? ''} ${m.apellidos ?? ''}`}
                            fotoURL={m.avatarUrl}
                            zoomable
                          />
                          <div>
                            <div className={styles.medicoName}>{m.nombreCompleto}</div>
                            <div className={styles.medicoEmail}>{m.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{m.colegiadoId}</td>
                      <td>{m.especialidad}</td>
                      <td>{rolBadge(m.role)}</td>
                      <td>{estadoBadge(m)}</td>
                      <td>
                        <span className={styles.salasCount}>
                          {m.salasAsignadas?.includes('*') ? 'Todas' : m.salasAsignadas?.length || 0}
                        </span>
                      </td>
                      <td>
                        <div className={styles.actions}>
                          {!puedeAdministrar && <span className={styles.soloAdmin}>Solo administradores y supervisores</span>}
                          {puedeAdministrar && m.role !== 'admin' && (
                            <>
                              <button
                                type="button"
                                className={styles.actionBtn}
                                onClick={() => setEditTarget({ tipo: 'medico', data: m })}
                                title="Editar datos del usuario"
                              >
                                <Pencil size={14} aria-hidden="true" />
                                <span>Editar</span>
                              </button>
                              {m.activo ? (
                                <button
                                  type="button"
                                  className={styles.actionBtnDanger}
                                  disabled={actionLoading === m.id}
                                  onClick={() => requestDeactivate(m)}
                                  title="Desactivar usuario"
                                >
                                  <UserX size={14} aria-hidden="true" />
                                  <span>Desactivar</span>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className={styles.actionBtnReactivate}
                                  disabled={actionLoading === m.id}
                                  onClick={() => requestReactivate(m)}
                                  title="Reactivar usuario"
                                >
                                  <UserCheck size={14} aria-hidden="true" />
                                  <span>Reactivar</span>
                                </button>
                              )}
                            </>
                          )}
                          {/* Cambiar rol (Médico / Supervisor / Administrador):
                              visible para cualquier usuario administrable,
                              también para degradar a un admin. */}
                          {puedeAdministrar && (
                            <button
                              type="button"
                              className={styles.actionBtn}
                              disabled={actionLoading === m.id}
                              onClick={() => requestRoleChange(m)}
                              title={`Cambiar rol (actual: ${labelRol(m.role)})`}
                            >
                              <Shield size={14} aria-hidden="true" />
                              <span>Rol</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Confirmation modal (deactivate / reactivate / role change) ─── */}
      <Modal
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.title || ''}
      >
        {confirm && (
          <div className={styles.confirmBody}>
            {confirm.action === 'desactivar' && (
              <div className={styles.confirmWarning}>
                <AlertTriangle size={18} aria-hidden="true" />
                <span>Esta acción revoca el acceso inmediato del usuario al sistema.</span>
              </div>
            )}

            <div className={styles.confirmMedico}>
              <Avatar
                className={styles.avatar}
                nombre={confirm.medico.nombreCompleto || `${confirm.medico.nombre ?? ''} ${confirm.medico.apellidos ?? ''}`}
                fotoURL={confirm.medico.avatarUrl}
              />
              <div>
                <div className={styles.confirmName}>{confirm.medico.nombreCompleto}</div>
                <div className={styles.confirmEmail}>{confirm.medico.email}</div>
              </div>
            </div>

            <p className={styles.confirmText}>{confirm.description}</p>

            {confirm.action === 'cambiar_rol' && (
              <div className={styles.roleSelectWrap}>
                {ROLES_ASIGNABLES.map((r) => (
                  <label
                    key={r.value}
                    className={`${styles.roleOption} ${confirm.nuevoRol === r.value ? styles.roleOptionActive : ''}`}
                  >
                    <input
                      type="radio"
                      name="nuevoRol"
                      value={r.value}
                      checked={confirm.nuevoRol === r.value}
                      onChange={() => setConfirm((c) => (c ? { ...c, nuevoRol: r.value } : c))}
                    />
                    <div>
                      <div className={styles.roleOptionLabel}>{r.label}</div>
                      <div className={styles.roleOptionHint}>{r.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant={confirm.variant === 'danger' ? 'danger' : 'primary'}
                loading={actionLoading === confirm.medico.id}
                disabled={confirm.action === 'cambiar_rol' && confirm.nuevoRol === confirm.medico.role}
                onClick={() => executeAction(confirm.medico.id, confirm.action, confirm.nuevoRol)}
              >
                {confirm.confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Pre-registro modal ─── */}
      <PreRegistroFormModal
        open={showPreRegistroModal}
        onClose={() => setShowPreRegistroModal(false)}
        onSuccess={() => setTab('preregistros')}
      />

      {/* ─── Editar médico / pre-registro ─── */}
      {editTarget && (
        <EditarMedicoModal
          open={true}
          onClose={() => setEditTarget(null)}
          tipo={editTarget.tipo}
          target={editTarget.data}
        />
      )}

      {/* ─── Confirmar eliminación de pre-registro ─── */}
      <Modal
        open={eliminarPreRegistro !== null}
        onClose={() => setEliminarPreRegistro(null)}
        title="Eliminar pre-registro"
      >
        {eliminarPreRegistro && (
          <div className={styles.confirmBody}>
            <div className={styles.confirmWarning}>
              <AlertTriangle size={18} aria-hidden="true" />
              <span>Esta acción elimina permanentemente el pre-registro pendiente.</span>
            </div>
            <div className={styles.confirmMedico}>
              <div className={styles.avatar} aria-hidden="true">
                {(eliminarPreRegistro.nombre?.[0] || '') +
                  (eliminarPreRegistro.apellidos?.[0] || '')}
              </div>
              <div>
                <div className={styles.confirmName}>
                  {eliminarPreRegistro.nombre} {eliminarPreRegistro.apellidos}
                </div>
                <div className={styles.confirmEmail}>{eliminarPreRegistro.email}</div>
              </div>
            </div>
            <p className={styles.confirmText}>
              El usuario no podrá crear su cuenta. Si más adelante desea invitarlo,
              deberá pre-registrarlo de nuevo.
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setEliminarPreRegistro(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={actionLoading === eliminarPreRegistro.id}
                onClick={() => handleEliminarPreRegistro(eliminarPreRegistro)}
              >
                Sí, eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
}
